import { ApiDimension, ApiUserActionUser } from "@incendium/api";
import { getAnalytisUsers } from "features/leadAnalytics";
import { TChartData } from "Interfaces";
import { useSelectedProject } from "Hooks";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_DEBOUNCE_MS = 200;

function useAnalyticsTableCustomers(debounceMs = DEFAULT_DEBOUNCE_MS) {
  const { selectedProject } = useSelectedProject();
  const { pathByName } = useProtectedRoutes();
  const [customersByLeadId, setCustomersByLeadId] = useState<
    Record<number, ApiUserActionUser>
  >({});

  const customersByLeadIdRef = useRef(customersByLeadId);
  const fetchingLeadIds = useRef<Set<number>>(new Set());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    customersByLeadIdRef.current = customersByLeadId;
  }, [customersByLeadId]);

  const getUserDashboardPath = useCallback(
    (leadId: number) => {
      if (!leadId || !selectedProject?.id || !selectedProject?.clientId) {
        return "";
      }

      return pathByName(RouteNames.USER_DASHBOARD_SINGLE, {
        clientId: selectedProject.clientId,
        projectId: selectedProject.id,
        leadid: leadId,
      });
    },
    [pathByName, selectedProject?.clientId, selectedProject?.id],
  );

  const fetchCustomersForRows = useCallback(
    async (rows: TChartData[]) => {
      if (!selectedProject?.id || rows.length === 0) {
        return;
      }

      const leadIds = [
        ...new Set(
          rows
            .map((row) => Number(row[ApiDimension.DIMENSION_LEAD_ID]))
            .filter(Boolean),
        ),
      ];

      const missingLeadIds = leadIds.filter(
        (id) =>
          !customersByLeadIdRef.current[id] &&
          !fetchingLeadIds.current.has(id),
      );

      if (missingLeadIds.length === 0) {
        return;
      }

      missingLeadIds.forEach((id) => fetchingLeadIds.current.add(id));

      try {
        const res = await getAnalytisUsers(
          selectedProject.id,
          missingLeadIds.length,
          "",
          undefined,
          undefined,
          missingLeadIds,
        );

        const fetched: Record<number, ApiUserActionUser> = {};
        (res.users || []).forEach((user: ApiUserActionUser) => {
          if (user.id) {
            fetched[user.id] = user;
          }
        });

        if (Object.keys(fetched).length > 0) {
          setCustomersByLeadId((prev) => ({ ...prev, ...fetched }));
        }
      } finally {
        missingLeadIds.forEach((id) => fetchingLeadIds.current.delete(id));
      }
    },
    [selectedProject?.id],
  );

  const onVisibleRowsChange = useCallback(
    (rows: TChartData[]) => {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        void fetchCustomersForRows(rows);
      }, debounceMs);
    },
    [debounceMs, fetchCustomersForRows],
  );

  useEffect(() => {
    return () => {
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const getCustomer = useCallback(
    (leadId: number) => customersByLeadId[leadId],
    [customersByLeadId],
  );

  return {
    customersByLeadId,
    getCustomer,
    getUserDashboardPath,
    onVisibleRowsChange,
  };
}

export default useAnalyticsTableCustomers;
